/**
 * Stage 1 of the hybrid track-changes engine — the word tokenizer.
 *
 * Splits a string into whole-word tokens where each token carries its **trailing** whitespace, so
 * the downstream word diff (T-004) defaults to whole-word redlines and positions reconstruct
 * exactly. The one hard guarantee is **round-trip**: `tokenize(s).join('') === s` for every input.
 * That is what lets the executor rebuild character offsets by summing token lengths right-to-left.
 *
 * Grounded by the T-001 spike, which pinned the diff baseline to the live `Range.search`/`range.text`
 * string. That string can contain field-result text fused to neighbours plus bidi/control marks
 * (LRM `U+200E`, RLM `U+200F`, field markers `U+0013`–`U+0015`, object anchor `U+FFFC`). None of
 * those are Unicode whitespace, so they ride inside word tokens untouched — the tokenizer never
 * splits or drops them. Making fields *editable-around* is a separate concern (T-007); here they
 * only have to survive the round-trip, which they do.
 *
 * Pure function, no Word/Office dependency — unit-tested under Vitest.
 *
 * ## Whitespace attachment rule
 * A token is `\S+\s*` — one run of non-whitespace followed by its trailing whitespace run. Newlines
 * and tabs are Unicode whitespace, so they attach as trailing whitespace to the word before them;
 * they never form part of a "word" and so cannot fuse two mismatched spans into a single token that
 * would fool the Levenshtein similarity threshold in Stage 3 (T-005).
 *
 * Two edge shapes fall outside `\S+\s*` and are handled explicitly to preserve round-trip:
 *  - **Leading whitespace** (the string starts with whitespace): emitted as its own leading token.
 *  - **All-whitespace string**: the whole string is that single leading token.
 * The empty string yields `[]`.
 */

/** Matches one word (non-whitespace run) plus its trailing whitespace run. */
const WORD_WITH_TRAILING_WS = /\S+\s*/g;

/**
 * Split `text` into whole-word tokens, each including its trailing whitespace.
 *
 * Guarantees `tokenize(text).join('') === text` for any input.
 *
 * @example
 * tokenize('We agree with the plan.')
 * // → ['We ', 'agree ', 'with ', 'the ', 'plan.']
 */
export function tokenize(text: string): string[] {
  if (text === '') return [];

  const tokens: string[] = [];

  // Leading whitespace has no preceding word to attach to, so it becomes its own token. This also
  // covers the all-whitespace case (the entire string is leading whitespace).
  const leading = /^\s+/.exec(text);
  let rest = text;
  if (leading) {
    tokens.push(leading[0]);
    rest = text.slice(leading[0].length);
    if (rest === '') return tokens; // all-whitespace input
  }

  // `rest` now starts with a non-whitespace char; consume word+trailing-whitespace runs to the end.
  // Because each match ends exactly where the next begins, there are no gaps: the concatenation of
  // all matches equals `rest`, so the full token list round-trips.
  WORD_WITH_TRAILING_WS.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = WORD_WITH_TRAILING_WS.exec(rest)) !== null) {
    tokens.push(match[0]);
  }

  return tokens;
}
